import { Button } from '../../components/ui/button';

// Confirmation modal for "Refresh from Campaign" (P2b-2 §7.4.6). Shows the
// count of content fields that will be reset — the number comes from
// diffCampaignVsRow applied to the currently-saved row state. Local
// unsaved edits aren't counted: the server operation refreshes the stored
// snapshot, then the client rebuilds draft state from the new row, so any
// pending local edits are superseded either way.
//
// Default focus is Cancel so muscle-memory "press Enter" doesn't blow away
// work. The Refresh button is primary styling but the focus ring lives on
// Cancel on open.

type Props = {
  count: number;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

export function RefreshFromCampaignModal({ count, busy, onCancel, onConfirm }: Props) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-slate-900/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="refresh-from-campaign-title"
    >
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 id="refresh-from-campaign-title" className="text-lg font-semibold text-slate-900">
          Refresh from Campaign?
        </h2>
        <p className="text-sm text-slate-700">
          This will reset all content fields on this row to the Campaign's current values.
          Any custom changes you've made will be lost.
        </p>
        <p className="text-sm text-slate-700">
          <strong>
            {count === 0
              ? 'No fields currently differ from the Campaign — nothing will change.'
              : `${count} field${count === 1 ? '' : 's'} will be reset.`}
          </strong>
        </p>
        <p className="text-xs text-slate-500">
          Budget, dates, AM, ready flags, execution fields, notes, and local assets are NOT
          reset — only snapshotted content fields.
        </p>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={busy}
            autoFocus
          >
            Cancel
          </Button>
          <Button type="button" onClick={onConfirm} disabled={busy || count === 0}>
            {busy ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
      </div>
    </div>
  );
}
